import React, { memo, useMemo, useState } from 'react';
import { EnhancedAlertBanner } from 'app/components/RsdForms/EnhancedAlertBanner';
import { useEventKeyDatePermissions } from 'app/utils/rbacHandler';
import { STATUS_VALUES } from 'utils/constants';
import { MonthlyDateBadge } from './MonthlyDateBadge';
import { MonthlyItemTitle } from './MonthlyItemTitle';
import { MonthlyMomentContextMenu } from './MonthlyMomentContextMenu';
import { MonthlyMomentDeleteDialog } from './MonthlyMomentDeleteDialog';
import {
  getMonthlyBadgeVariant,
  isMonthlyEventPast
} from '../utils/getMonthlyBadgeVariant';
import { useMonthlyEventHighlight } from '../utils/monthlyEventHighlight';
import { useMonthlyEventDoubleClick } from '../hooks/useMonthlyEventDoubleClick';
import { useMonthlyEventActions } from '../hooks/useMonthlyKeyDateActions';
import { useDelayedTooltip } from '../hooks/useDelayedTooltip';
import { useMonthlyUpdateSuccessHighlight } from '../hooks/useMonthlyUpdateSuccessHighlight';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyEventInlineItemProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  className?: string;
}

export const MonthlyEventInlineItem = memo(
  ({ item, laneLabel, className }: MonthlyEventInlineItemProps) => {
    const { isAdmin } = useEventKeyDatePermissions();
    const [isHovered, setIsHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const tooltip = useDelayedTooltip();

    const {
      highLightCards,
      isHighlighted,
      showHighlight,
      isHighlightSource,
      implementedCount
    } = useMonthlyEventHighlight(item, laneLabel);
    const { handleDoubleClick } = useMonthlyEventDoubleClick(item, laneLabel);
    const {
      notification,
      setNotification,
      selectedMoment,
      isEditModalOpen,
      showDeleteConfirmation,
      handleMenuAction,
      handleCloseEditModal,
      handleEditSuccess,
      handleConfirmDelete,
      handleCancelDelete,
      editInitialValues,
      itemType,
      deleteTitle,
      deleteMessage,
      deleteConfirmLabel,
      EditEventModal
    } = useMonthlyEventActions();

    const { isCompleted } = useMemo(
      () => getMonthlyBadgeVariant(item, laneLabel),
      [item, laneLabel]
    );

    const isPastEvent = useMemo(() => isMonthlyEventPast(item), [item]);
    const showDisabledRow = isCompleted || isPastEvent;
    const issueId = item.issueId || item.id;
    const isTemporarilyHighlighted = useMonthlyUpdateSuccessHighlight(
      issueId,
      item.releasePhaseId
    );

    const showEventAdminMenu =
      isAdmin &&
      item.projectType === 'Event' &&
      item.status === STATUS_VALUES.ACTIVE &&
      item.issueId &&
      item.title &&
      (isHovered || isMenuOpen);

    return (
      <>
        {notification && (
          <EnhancedAlertBanner
            variant={notification.variant}
            message={notification.message}
            onClose={() => setNotification(null)}
            autoHide={true}
            autoHideDelay={4000}
          />
        )}

        <div
          className={`${styles.monthlyInlineItem} ${
            styles.monthlyInlineItemMainCard
          } ${showDisabledRow ? styles.monthlyInlineItemCompleted : ''} ${
            className || ''
          } ${styles.monthlyInlineItemEvent} ${
            showHighlight || isHighlightSource
              ? styles.monthlyInlineItemHighlighted
              : ''
          } ${
            isTemporarilyHighlighted
              ? styles.monthlyInlineItemUpdateHighlight
              : ''
          }`}
          onDoubleClick={handleDoubleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className={styles.monthlyInlineItemMain}>
            <MonthlyDateBadge
              item={item}
              laneLabel={laneLabel}
              useCompletedStyle={showDisabledRow}
              onClick={highLightCards}
            />
            <div className={styles.monthlyInlineItemBody}>
              <div className={styles.monthlyItemTitleWithMenu}>
                <MonthlyItemTitle
                  title={item.title || 'Untitled'}
                  projectType={item.projectType || item.type}
                  releasePhase={item.releasePhase}
                  releasePhaseId={item.releasePhaseId}
                  releasePhases={item.releasePhases}
                  laneLabel={laneLabel}
                  issueId={issueId}
                  onPress={highLightCards}
                  tooltip={tooltip}
                />
                {showEventAdminMenu && (
                  <div
                    className={styles.monthlyMomentMenuInline}
                    onClick={event => event.stopPropagation()}
                  >
                    <MonthlyMomentContextMenu
                      onAction={action => handleMenuAction(action, item)}
                      onOpenChange={setIsMenuOpen}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {(isHovered || isHighlighted) && (
            <span
              className={styles.monthlyEventRelationshipCount}
              onMouseLeave={() => setIsHovered(false)}
            >
              {implementedCount}
            </span>
          )}
        </div>

        {isEditModalOpen && selectedMoment && editInitialValues && (
          <EditEventModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
            itemType={itemType}
            issueId={selectedMoment.issueId!}
            initialValues={editInitialValues}
          />
        )}

        {showDeleteConfirmation && selectedMoment && (
          <MonthlyMomentDeleteDialog
            title={deleteTitle}
            message={deleteMessage}
            confirmLabel={deleteConfirmLabel}
            onCancel={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        )}
      </>
    );
  }
);
